<template>
  <div
    id="page-care"
    class="page-care"
  >
    <care-banner />
    <div
      class="page-care__scroll"
      :class="`active-${sectionIndex}`"
    >
      <section class="page-care__section">
        <care-breathing-section />
      </section>
      <section class="page-care__section">
        <care-snap-cute-section />
      </section>
      <section class="page-care__section">
        <care-sleep-section />
      </section>
      <section class="page-care__section">
        <care-practice-section />
      </section>
      <care-phone :sectionIndex="sectionIndex"/>
      <div class="page-care__mobile-bg">
        <div
          :style="{
            backgroundImage: 'url(/media/img/products/care/section-2-m.jpg)'
          }"
        />
        <div
          :style="{
            backgroundImage: 'url(/media/img/products/care/section-3-m.jpg)'
          }"
        />
      </div>
    </div>
    <care-plan-block />
    <care-faq />
  </div>
</template>
<script>
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { mapState } from 'vuex';

import languageList from '../configs/languageList';
import CareBanner from '../components/care/CareBanner.vue';
import CarePhone from '../components/care/CarePhone.vue';
import CareBreathingSection from '../components/care/CareBreathingSection.vue';
import CareSnapCuteSection from '../components/care/CareSnapCuteSection.vue';
import CareSleepSection from '../components/care/CareSleepSection.vue';
import CarePracticeSection from '../components/care/CarePracticeSection.vue';
import CarePlanBlock from '../components/care/CarePlanBlock.vue';
import CareFaq from '../components/care/CareFaq.vue';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(ScrollToPlugin);
ScrollTrigger.config({ ignoreMobileResize: true });

let tl;
const DURATION = 1;
const scrolling = {
  enabled: true,
  events: 'scroll,wheel,touchmove,pointermove'.split(','),
  prevent: (e) => e.preventDefault(),
  disable() {
    if (scrolling.enabled) {
      scrolling.enabled = false;
      window.addEventListener('scroll', gsap.ticker.tick, { passive: true });
      scrolling.events.forEach(
        (e, i) => (i ? document : window)
          .addEventListener(e, scrolling.prevent, { passive: false }),
      );
      document.body.style.touchAction = 'none';
    }
  },
  enable() {
    if (!scrolling.enabled) {
      scrolling.enabled = true;
      window.removeEventListener('scroll', gsap.ticker.tick);
      scrolling.events.forEach(
        (e, i) => (i ? document : window)
          .removeEventListener(e, scrolling.prevent),
      );
      document.body.style.touchAction = 'auto';
    }
  },
};

export default {
  name: 'Care',
  components: {
    CareBanner,
    CarePhone,
    CareBreathingSection,
    CareSnapCuteSection,
    CareSleepSection,
    CarePracticeSection,
    CarePlanBlock,
    CareFaq,
  },
  headMetaInfo() {
    return {
      title: this.$t('headMetaInfo.care.title'),
      titleTemplate: `%s - ${this.$t('headMetaInfo.default.titleTemplate')}`,
      meta: [
        { name: 'description', content: this.$t('headMetaInfo.care.desc') },
        { property: 'og:title', content: this.$t('headMetaInfo.care.title') },
        { property: 'og:description', content: this.$t('headMetaInfo.care.desc') },
      ],
      htmlAttrs: {
        lang: this.$i18n.locale,
      },
      link: (() => {
        // Ugly code, could be better!
        // { rel: 'canonical', href: `${window.location.origin}/` }
        // { rel: 'alternate', hreflang: this.$i18n.locale, href: `${window.location.origin}/` }
        const linkArray = [];
        if (process.env.NODE_ENV === 'prerender') {
          const baseUrl = process.env.BASE_URL.startsWith('http') ? process.env.BASE_URL : 'https://www.lollipop.camera';
          linkArray.push({ rel: 'canonical', href: `${baseUrl}/product/care` });
          languageList.forEach((lang) => {
            linkArray.push({ rel: 'alternate', hreflang: lang, href: `${baseUrl}/${lang}/product/care` });
          });
        }
        return linkArray;
      })(),
    };
  },
  data() {
    return {
      sectionIndex: 0,
      sectionsData: [
        {},
        {},
        {},
        {},
      ],
    };
  },
  computed: {
    ...mapState({
      isMobile: (state) => state.isMobile,
    }),
  },
  mounted() {
    const sectionLength = this.sectionsData.length;
    tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.page-care__scroll',
        start: 'top top',
        end: `${sectionLength * 100}%`,
        pin: true,
        scrub: true,
        ease: 'none',
        // markers: true,
        onUpdate: (scrollTrigger) => {
          if (scrolling.enabled) {
            scrolling.disable();

            const baseTop = scrollTrigger.pin.parentElement.offsetTop;
            const maxIndex = sectionLength + 1;
            this.sectionIndex = (this.sectionIndex + scrollTrigger.direction + maxIndex) % maxIndex;

            if (scrollTrigger.direction < 0 && this.sectionIndex === 1) {
              this.sectionIndex = 0;
            }
            const y = this.sectionIndex * window.innerHeight + baseTop;

            gsap.to(window, {
              scrollTo: { y, autoKill: false },
              onComplete: () => {
                setTimeout(() => {
                  scrolling.enable();
                }, 100);
              },
              duration: DURATION,
            });
          }
        },
      },
    });
  },
  beforeDestroy() {
    tl.kill();
  },
  methods: {

  },
};
</script>

<style lang="sass" scoped>
.container
  width: 100%;
  padding: 0 9vw;

  @media screen and (max-width: 1440px)
    padding: 0 3vw;

  @media screen and (max-width: 414px)
    padding: 0 30px;

  @media screen and (max-width: 375px)
    padding: 0 25px;

.page-care
  overflow: hidden

  &__scroll
    position: relative
    width: 100%
    height: 100vh

  &__section
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%

  &__mobile-bg
    display: none
    position: absolute
    top: 0
    left: 0
    border-radius: 0 250px 250px 0
    width: 300px
    height: 450px
    transform: translateZ(0px)
    pointer-events: none
    overflow: hidden
    opacity: 0;

    .page-care__scroll.active-0 &,
    .page-care__scroll.active-1 &,
    .page-care__scroll.active-2 &,
      opacity: 1

    .page-care__scroll.active-2 &,
    .page-care__scroll.active-3 &,
        transition: opacity 0.5s ease-in-out

    > *
      width: 100%
      height: 100%
      background: #fff no-repeat center / cover
      transition: transform 1s ease-in-out

      .page-care__scroll.active-2 &,
      .page-care__scroll.active-3 &,
        transform: translateY(-100%)

    @media screen and (orientation: portrait), (max-width: 768px)
      display: block

    @media screen and (max-width: 375px)
      width: 250px
      height: 375px

</style>
